import { type ChangeEvent, useMemo, useState } from "react";
import type { VesselHistorySelection, VesselNoonFilters, VesselNoonReport } from "../models/vessel-data.models";

const emptyFilters: VesselNoonFilters = {
  dateFrom: "",
  dateTo: "",
  voyage: [],
  vesselCondition: [],
  location: [],
  minimumStreamingHours: "",
  maximumStreamingHours: "",
  minimumTotalConsumption: "",
  maximumTotalConsumption: "",
  minimumWindBeaufort: "",
  maximumWindBeaufort: "",
  minimumTotalMeConsumption: "",
  maximumTotalMeConsumption: "",
  minimumTotalAuxConsumption: "",
  maximumTotalAuxConsumption: "",
  minimumCombinedWaveHeight: "",
  maximumCombinedWaveHeight: "",
};

const noonReportColumns: Array<keyof VesselNoonReport> = [
  "formGuid", "formLink", "imoNumber", "vesselName", "voyageNumber", "date", "reportType", "steamingHours", "location", "vesselCondition",
  "midDraft", "observedDistance", "mainEngineRpm", "mainEnginePowerPercent", "mainEnginePowerKw", "specificFuelConsumption", "speedOverGround",
  "calculatedSpeedThroughWater", "reportedSpeedThroughWater", "totalMainEngineConsumption24h", "totalAuxConsumption24h", "totalConsumption24h",
  "windBeaufort", "combinedWaveHeight", "goodWeather", "robIfo", "robLsf", "robLsg", "robMgo", "totalDgConsumption24h", "boilerConsumption24h",
  "distanceToGo", "portName", "slipPercent", "turboChargerRpm", "hasScrubber", "scrubberStoppage", "consumptionIfo", "consumptionLsf",
  "consumptionLsg", "consumptionMgo", "totalMainEngineConsumption", "mainEngineIfo", "mainEngineLsf", "mainEngineLsg", "mainEngineMgo",
  "totalAuxConsumption", "auxIfo", "auxLsf", "auxLsg", "auxMgo", "dg1Consumption", "dg2Consumption", "dg3Consumption", "boilerConsumption",
  "combinedWaveDirection", "windSpeed", "windDirection", "currentFactor", "seaWaterSalinity", "seaWaterTemperature", "noonSwell", "noonWindForce",
  "noonSeaState", "swellWaveDirection", "swellWaveHeight", "swellWavePeriod", "windSeaWaveDirection", "windSeaWaveHeight", "windSeaWavePeriod",
];

const reportHeading = (key: string) => key.replace(/([A-Z])/g, " $1").replace(/^./, (character) => character.toUpperCase());
const reportValue = (value: VesselNoonReport[keyof VesselNoonReport]) => value === null || value === "" ? "—" : typeof value === "boolean" ? (value ? "True" : "False") : String(value);

const numberAllows = (value: number | null, boundary: string, mode: "minimum" | "maximum") => {
  if (!boundary) {
    return true;
  }

  return value !== null && (mode === "minimum" ? value >= Number(boundary) : value <= Number(boundary));
};

const average = (values: Array<number | null>): string => {
  const available = values.filter((value): value is number => value !== null);

  return available.length ? (available.reduce((total, value) => total + value, 0) / available.length).toFixed(2) : "—";
};

// Combines the PBIP noon/weather slicers against the already authorized vessel response.
const VesselNoonReports = ({ reports, onSelect }: { reports: VesselNoonReport[]; onSelect: (selection: VesselHistorySelection) => void }) => {
  const [filters, setFilters] = useState(emptyFilters);
  const filtered = useMemo(() => reports.filter((report) =>
    (!filters.dateFrom || Boolean(report.date && report.date.slice(0, 10) >= filters.dateFrom)) &&
    (!filters.dateTo || Boolean(report.date && report.date.slice(0, 10) <= filters.dateTo)) &&
    (!filters.voyage.length || filters.voyage.includes(report.voyageNumber)) &&
    (!filters.vesselCondition.length || Boolean(report.vesselCondition && filters.vesselCondition.includes(report.vesselCondition))) &&
    (!filters.location.length || Boolean(report.location && filters.location.includes(report.location))) &&
    numberAllows(report.steamingHours, filters.minimumStreamingHours, "minimum") &&
    numberAllows(report.steamingHours, filters.maximumStreamingHours, "maximum") &&
    numberAllows(report.totalConsumption24h, filters.minimumTotalConsumption, "minimum") &&
    numberAllows(report.totalConsumption24h, filters.maximumTotalConsumption, "maximum") &&
    numberAllows(report.windBeaufort, filters.minimumWindBeaufort, "minimum") &&
    numberAllows(report.windBeaufort, filters.maximumWindBeaufort, "maximum") &&
    numberAllows(report.totalMainEngineConsumption24h, filters.minimumTotalMeConsumption, "minimum") &&
    numberAllows(report.totalMainEngineConsumption24h, filters.maximumTotalMeConsumption, "maximum") &&
    numberAllows(report.totalAuxConsumption24h, filters.minimumTotalAuxConsumption, "minimum") &&
    numberAllows(report.totalAuxConsumption24h, filters.maximumTotalAuxConsumption, "maximum") &&
    numberAllows(report.combinedWaveHeight, filters.minimumCombinedWaveHeight, "minimum") &&
    numberAllows(report.combinedWaveHeight, filters.maximumCombinedWaveHeight, "maximum")
  ), [filters, reports]);

  const averages = useMemo(() => {
    const reportsByLocation = new Map<string, VesselNoonReport[]>();

    filtered.forEach((report) => {
      const location = report.location ?? "—";
      reportsByLocation.set(location, [...(reportsByLocation.get(location) ?? []), report]);
    });

    return Array.from(reportsByLocation.entries())
      .sort(([left], [right]) => right.localeCompare(left))
      .map(([location, locationReports]) => ({
        location,
        values: [
          average(locationReports.map((report) => report.midDraft)),
          average(locationReports.map((report) => report.speedOverGround)),
          average(locationReports.map((report) => report.calculatedSpeedThroughWater)),
          average(locationReports.map((report) => report.reportedSpeedThroughWater)),
          average(locationReports.map((report) => report.totalMainEngineConsumption24h)),
          average(locationReports.map((report) => report.totalAuxConsumption24h)),
          average(locationReports.map((report) => report.totalConsumption24h)),
          average(locationReports.map((report) => report.totalDgConsumption24h)),
          average(locationReports.map((report) => report.boilerConsumption24h)),
          average(locationReports.map((report) => report.seaWaterTemperature)),
        ],
      }));
  }, [filtered]);

  const update = <Key extends keyof VesselNoonFilters>(key: Key, value: VesselNoonFilters[Key]) => setFilters((current) => ({ ...current, [key]: value }));
  const choices = (key: "voyage" | "vesselCondition" | "location") => Array.from(new Set(reports.map((report) => key === "voyage" ? report.voyageNumber : report[key]).filter(Boolean))) as string[];
  const selectedValues = (event: ChangeEvent<HTMLSelectElement>) => Array.from(event.target.selectedOptions, (option) => option.value);
  const openNoonReport = (report: VesselNoonReport) => onSelect({ kind: "noon", title: `Noon report ${report.date ?? "record"}`, values: report });

  return (
    <section className="overflow-hidden rounded-xl border bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" data-test="vessel-data-noon-reports">
      <div className="flex items-center justify-between border-b px-4 py-3 dark:border-gray-700">
        <div><h2 className="text-sm font-semibold">Noon and weather reports</h2><p className="text-[11px] text-gray-500">{filtered.length} of {reports.length} reports · select a row for every PBIP field</p></div>
        <button type="button" onClick={() => setFilters(emptyFilters)} className="text-xs font-semibold text-green" data-test="vessel-data-noon-clear-filters">Clear filters</button>
      </div>
      <div className="grid gap-2 border-b bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30 sm:grid-cols-3 xl:grid-cols-6">
        <input type="date" value={filters.dateFrom} onChange={(event) => update("dateFrom", event.target.value)} aria-label="Report date from" data-test="vessel-data-filter-date-from" className="rounded border px-2 py-2 text-xs dark:bg-gray-800" />
        <input type="date" value={filters.dateTo} onChange={(event) => update("dateTo", event.target.value)} aria-label="Report date to" data-test="vessel-data-filter-date-to" className="rounded border px-2 py-2 text-xs dark:bg-gray-800" />
        {(["voyage", "vesselCondition", "location"] as const).map((key) => (
          <select key={key} multiple value={filters[key]} onChange={(event) => update(key, selectedValues(event))} aria-label={`Filter by ${key}; multiple values allowed`} data-test={`vessel-data-filter-${key}`} className="min-h-20 rounded border px-2 py-2 text-xs dark:bg-gray-800">
            {choices(key).map((choice) => <option key={choice}>{choice}</option>)}
          </select>
        ))}
        {(["minimumStreamingHours", "maximumStreamingHours", "minimumTotalConsumption", "maximumTotalConsumption", "minimumWindBeaufort", "maximumWindBeaufort", "minimumTotalMeConsumption", "maximumTotalMeConsumption", "minimumTotalAuxConsumption", "maximumTotalAuxConsumption", "minimumCombinedWaveHeight", "maximumCombinedWaveHeight"] as const).map((key) => <input key={key} type="number" value={filters[key]} onChange={(event) => update(key, event.target.value)} placeholder={key.replace("minimum", "Min ").replace("maximum", "Max ").replace(/([A-Z])/g, " $1")} aria-label={key} data-test={`vessel-data-filter-${key}`} className="rounded border px-2 py-2 text-xs dark:bg-gray-800" />)}
      </div>
      <div className="overflow-x-auto border-b p-3 dark:border-gray-700" data-test="vessel-data-noon-averages">
        <table className="w-full min-w-[1050px] text-xs">
          <caption className="mb-2 text-left font-semibold">Filtered averages by location</caption>
          <thead><tr className="text-left text-[10px] uppercase text-gray-500">{["Location", "Mid draft", "SOG", "Calculated STW", "Reported STW", "Total ME 24h", "Total Aux 24h", "Total cons 24h", "Total DG 24h", "Boiler 24h", "Sea temperature"].map((heading) => <th key={heading} className="pr-4">{heading}</th>)}</tr></thead>
          <tbody>{averages.map((row) => <tr key={row.location}><td className="pr-4 pt-2 font-semibold">{row.location}</td>{row.values.map((value, index) => <td key={`${row.location}-${index}`} className="pr-4 pt-2 font-semibold">{value}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[8000px] text-xs" data-test="vessel-data-noon-table">
          <thead><tr className="border-b text-left text-gray-500">{noonReportColumns.map((key) => <th key={key} className="px-3 py-2">{reportHeading(key)}</th>)}</tr></thead>
          <tbody>{filtered.map((report, index) => <tr key={`${report.formGuid}-${index}`} tabIndex={0} aria-label={`Open noon report ${report.date ?? "record"} details`} data-test={`vessel-data-noon-row-${index}`} onClick={() => openNoonReport(report)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openNoonReport(report); } }} className="cursor-pointer border-b hover:bg-primary-10 focus:bg-primary-10">{noonReportColumns.map((key) => <td key={key} className="px-3 py-3">{reportValue(report[key])}</td>)}</tr>)}</tbody>
        </table>
        {!filtered.length && <p className="p-5 text-sm text-gray-500">No reports match the combined filters.</p>}
      </div>
    </section>
  );
};

export default VesselNoonReports;
